import React from 'react';

import { ColumnDef } from '@tanstack/react-table';
import moment from 'moment-timezone';

import { ITableColumnState } from 'hooks/useTableCurrentState';

import { ReportList } from 'api/endPoints/getReportList';
import { Assignee } from 'api/queries/getTopStartDateChanges.query';

import { AssigneeAvatar } from 'components/common/AssigneeAvatar/AssigneeAvatar';
import { DisplayAvatarListWithHiddenItemCell } from 'components/common/Tables/Cells/DisplayAvatarListWithHiddenItemCell/DisplayAvatarListWithHiddenItemCell';
import { ReportLinkWithFavouritesCell } from 'components/common/Tables/Cells/ReportLinkWithFavouritesCell/ReportLinkWithFavouritesCell';
import { ReportProjectsPillsCell } from 'components/common/Tables/Cells/ReportProjectsPillsCell/ReportProjectsPillsCell';
import { ReportTagsCell } from 'components/common/Tables/Cells/ReportTagsCell/ReportTagsCell';
import { columnsCommonConfig } from 'components/common/Tables/columnsCommonConfig';

import { REPORT_HAS_NO_ACCESS_RESTRICTION } from 'components/widgets/ReportsHome/ReportsListTable/ReportsListTable.consts';

const MAX_NUMBER_OF_SHARED_REPORT_PEOPLE_TO_DISPLAY = 4;

export const columnsV8: ColumnDef<ReportList>[] = [
    {
        header: 'Report Name',
        ...columnsCommonConfig.reportName,
        accessorKey: 'name',
        filterFn: (row, columnId, filterValue) => {
            if (!filterValue || filterValue === '') return true;
            const lowerCaseFilterValue = filterValue.toLowerCase();
            const isReportNameContainsSearch = row.original.name.toLowerCase().includes(lowerCaseFilterValue);
            const isReportProjectsContainsSearch = row.original.projContrDetails.some(
                (projectContract) =>
                    projectContract.projectName.toLowerCase().includes(lowerCaseFilterValue) ||
                    projectContract.contractName.toLowerCase().includes(lowerCaseFilterValue),
            );
            return isReportNameContainsSearch || isReportProjectsContainsSearch;
        },
        cell: ({ row }) => {
            const { id, name, isMyFavorite } = row.original;
            return <ReportLinkWithFavouritesCell isFavorite={isMyFavorite} id={id} name={name} />;
        },
        sortingFn: 'alphanumeric',
    },
    {
        ...columnsCommonConfig.reportProjects,
        accessorFn: (row) => {
            const firstComponent = row.projContrDetails?.[0];
            return firstComponent ? firstComponent.projectName : '';
        },
        filterFn: (row, columnId, filterValue) => {
            if (filterValue.length === 0) return true;

            const projectNames = filterValue.map((project) => project.value);
            const filterResult = row.original.projContrDetails.some((project) => {
                return projectNames.includes(project.projectName);
            });
            return filterResult;
        },
        header: 'Source Projects (Data Date)',
        cell: ({ row }) => {
            const projectsInformation = row.original.projContrDetails;
            return <ReportProjectsPillsCell projectList={projectsInformation} />;
        },
    },
    {
        header: 'Access',
        ...columnsCommonConfig.sharedWith,
        cell: ({ row }) => {
            if (row.original.isAccessListDefault) return <div>{REPORT_HAS_NO_ACCESS_RESTRICTION}</div>;
            const sortedAccessList = row.original.accessList
                ? [...row.original.accessList].sort((a, b) => a.username.localeCompare(b.username))
                : [];
            return (
                <DisplayAvatarListWithHiddenItemCell
                    avatarList={sortedAccessList}
                    maxNumberToDisplay={MAX_NUMBER_OF_SHARED_REPORT_PEOPLE_TO_DISPLAY}
                />
            );
        },
    },
    {
        header: 'Modified at',
        accessorKey: 'lastUpdateDate',
        ...columnsCommonConfig.lastModified,
        cell: ({ row }) => {
            return <>{moment(row.original.lastUpdateDate).fromNow()}</>;
        },
    },
    {
        header: 'Modified by',
        accessorFn: (row) => {
            return `${row.lastUpdateUser.lastName} ${row.lastUpdateUser.firstName}`;
        },
        ...columnsCommonConfig.reportLastUserUpdated,
        cell: ({ row }) => {
            if (!row.original.lastUpdateUser) return null;
            const userAssignee: Assignee = {
                userName: row.original.lastUpdateUser.username,
                id: null,
                firstName: row.original.lastUpdateUser.firstName ?? '',
                lastName: row.original.lastUpdateUser.lastName ?? '',
                imageName: row.original.lastUpdateUser.image,
            };
            return <AssigneeAvatar assignee={userAssignee} />;
        },
        sortingFn: 'alphanumeric',
    },

    {
        header: 'Tags',
        accessorFn: (row) => {
            const firstTag = row.tags?.[0];
            return firstTag?.name || '';
        },
        ...columnsCommonConfig.reportTags,
        filterFn: (row, columnId, filterValue) => {
            if (!filterValue) return true;
            return row.original.tags.some((tag) => tag.id === filterValue);
        },
        cell: ({ row }) => {
            return <ReportTagsCell row={row} />;
        },
        sortingFn: 'alphanumeric',
    },
    {
        id: 'isMyFavorite',
        accessorKey: 'isMyFavorite',
        filterFn: (row, columnId, filterValue) => {
            if (filterValue) {
                return row.original.isMyFavorite;
            }
            return true;
        },
    },
];

export const initialState: ITableColumnState = {
    hiddenColumns: ['isMyFavorite'],
    sortBy: [
        {
            id: 'lastModified',
            desc: true,
        },
    ],
    columnOrder: columnsV8.map((column) => column.id),
};
